'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Mic2, Send, Loader2, MapPin, Mail, Phone } from 'lucide-react';

interface BookingFormData {
  clientName: string;
  company: string;
  email: string;
  phone: string;
  country: string;
  city: string;
  eventType: string;
  eventDate: string;
  venue: string;
  guestCount: string;
  budget: string;
  message: string;
}

const eventTypes = ['Concert', 'Festival', 'Corporate Event', 'Private Event', 'Wedding', 'Tour Date', 'TV/Radio Appearance', 'Brand Activation'];

export default function BookingCTA() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { register, handleSubmit, reset, formState: { errors } } = useForm<BookingFormData>();

  const onSubmit = async (data: BookingFormData) => {
    setIsSubmitting(true);
    try {
      const res = await fetch('/api/bookings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      const result = await res.json();
      if (!res.ok) {
        toast.error(result.error ?? 'Failed to submit booking. Please try again.');
        return;
      }
      reset();
      toast.success(`Booking inquiry received! Reference: ${result.reference}. Our team will respond within 24 hours.`);
    } catch {
      toast.error('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="booking" className="py-24 bg-background">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Left info */}
          <div className="lg:col-span-4">
            <p className="text-[11px] font-600 tracking-widest uppercase text-primary mb-2">Bookings</p>
            <h2 className="text-4xl font-800 text-foreground mb-4">Book Hadatha</h2>
            <p className="text-muted-foreground text-sm leading-relaxed mb-8">
              Available for concerts, festivals, corporate events, private functions, and brand activations across East Africa and internationally. Submit your inquiry and our booking team will respond within 24 hours.
            </p>

            <div className="space-y-4 mb-8">
              {[
                { icon: Mic2, label: 'Performance Types', value: 'Concerts · Festivals · Corporate · Private' },
                { icon: MapPin, label: 'Coverage', value: 'Tanzania, East Africa & International' },
                { icon: Mail, label: 'Booking Email', value: 'info@hadathatz.com' },
                { icon: Phone, label: 'WhatsApp / Call', value: '+255 718 863 913' },
              ].map((item) => (
                <div key={`booking-info-${item.label}`} className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-primary/10 text-primary mt-0.5 shrink-0">
                    <item.icon size={16} />
                  </div>
                  <div>
                    <p className="text-[11px] font-600 text-muted-foreground uppercase tracking-wider">{item.label}</p>
                    <p className="text-sm text-foreground">{item.value}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="p-4 rounded-xl bg-primary/10 border border-primary/20">
              <p className="text-xs font-600 text-primary mb-1">Direct Booking</p>
              <p className="text-sm text-foreground mb-3">For urgent bookings, contact us directly:</p>
              <a
                href="tel:+255718863913"
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-gold text-primary-foreground text-sm font-700 hover:opacity-90 active:scale-95 transition-all w-full justify-center"
              >
                <Phone size={16} />
                Call +255 718 863 913
              </a>
            </div>
          </div>

          {/* Booking form */}
          <div className="lg:col-span-8">
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="bg-card border border-border rounded-2xl p-8 card-glow"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {/* Client name */}
                <div>
                  <label className="block text-xs font-600 text-foreground mb-1.5">
                    Full Name <span className="text-negative">*</span>
                  </label>
                  <input
                    {...register('clientName', { required: 'Full name is required' })}
                    type="text"
                    placeholder="Your full name"
                    className="w-full px-4 py-2.5 text-sm bg-input border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring focus:border-primary/40 transition-all"
                  />
                  {errors.clientName && <p className="mt-1 text-xs text-negative">{errors.clientName.message}</p>}
                </div>

                {/* Company */}
                <div>
                  <label className="block text-xs font-600 text-foreground mb-1.5">Company / Organization</label>
                  <input
                    {...register('company')}
                    type="text"
                    placeholder="Event company or organization"
                    className="w-full px-4 py-2.5 text-sm bg-input border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring focus:border-primary/40 transition-all"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-xs font-600 text-foreground mb-1.5">
                    Email <span className="text-negative">*</span>
                  </label>
                  <input
                    {...register('email', {
                      required: 'Email is required',
                      pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email address' }
                    })}
                    type="email"
                    placeholder="booking@yourcompany.com"
                    className="w-full px-4 py-2.5 text-sm bg-input border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring focus:border-primary/40 transition-all"
                  />
                  {errors.email && <p className="mt-1 text-xs text-negative">{errors.email.message}</p>}
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-xs font-600 text-foreground mb-1.5">
                    Phone / WhatsApp <span className="text-negative">*</span>
                  </label>
                  <input
                    {...register('phone', { required: 'Phone number is required' })}
                    type="tel"
                    placeholder="+255 xxx xxx xxx"
                    className="w-full px-4 py-2.5 text-sm bg-input border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring focus:border-primary/40 transition-all"
                  />
                  {errors.phone && <p className="mt-1 text-xs text-negative">{errors.phone.message}</p>}
                </div>

                {/* Country */}
                <div>
                  <label className="block text-xs font-600 text-foreground mb-1.5">Country <span className="text-negative">*</span></label>
                  <input
                    {...register('country', { required: 'Country is required' })}
                    type="text"
                    placeholder="Tanzania, Kenya, Uganda..."
                    className="w-full px-4 py-2.5 text-sm bg-input border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring focus:border-primary/40 transition-all"
                  />
                  {errors.country && <p className="mt-1 text-xs text-negative">{errors.country.message}</p>}
                </div>

                {/* City */}
                <div>
                  <label className="block text-xs font-600 text-foreground mb-1.5">City</label>
                  <input
                    {...register('city')}
                    type="text"
                    placeholder="City of the event"
                    className="w-full px-4 py-2.5 text-sm bg-input border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring focus:border-primary/40 transition-all"
                  />
                </div>

                {/* Event type */}
                <div>
                  <label className="block text-xs font-600 text-foreground mb-1.5">
                    Event Type <span className="text-negative">*</span>
                  </label>
                  <select
                    {...register('eventType', { required: 'Event type is required' })}
                    className="w-full px-4 py-2.5 text-sm bg-input border border-border rounded-lg text-foreground focus:outline-none focus:ring-1 focus:ring-ring focus:border-primary/40 transition-all"
                  >
                    <option value="">Select event type</option>
                    {eventTypes.map((t) => (
                      <option key={`event-type-${t}`} value={t}>{t}</option>
                    ))}
                  </select>
                  {errors.eventType && <p className="mt-1 text-xs text-negative">{errors.eventType.message}</p>}
                </div>

                {/* Event date */}
                <div>
                  <label className="block text-xs font-600 text-foreground mb-1.5">
                    Event Date <span className="text-negative">*</span>
                  </label>
                  <input
                    {...register('eventDate', { required: 'Event date is required' })}
                    type="date"
                    className="w-full px-4 py-2.5 text-sm bg-input border border-border rounded-lg text-foreground focus:outline-none focus:ring-1 focus:ring-ring focus:border-primary/40 transition-all"
                  />
                  {errors.eventDate && <p className="mt-1 text-xs text-negative">{errors.eventDate.message}</p>}
                </div>

                {/* Venue */}
                <div>
                  <label className="block text-xs font-600 text-foreground mb-1.5">Venue / Location</label>
                  <input
                    {...register('venue')}
                    type="text"
                    placeholder="Venue name and address"
                    className="w-full px-4 py-2.5 text-sm bg-input border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring focus:border-primary/40 transition-all"
                  />
                </div>

                {/* Budget */}
                <div>
                  <label className="block text-xs font-600 text-foreground mb-1.5">
                    Budget Range
                    <span className="ml-1 font-400 text-muted-foreground">(USD/TZS)</span>
                  </label>
                  <select
                    {...register('budget')}
                    className="w-full px-4 py-2.5 text-sm bg-input border border-border rounded-lg text-foreground focus:outline-none focus:ring-1 focus:ring-ring focus:border-primary/40 transition-all"
                  >
                    <option value="">Select budget range</option>
                    <option value="under-1k">Under $1,000</option>
                    <option value="1k-5k">$1,000 – $5,000</option>
                    <option value="5k-15k">$5,000 – $15,000</option>
                    <option value="15k-30k">$15,000 – $30,000</option>
                    <option value="30k-plus">$30,000+</option>
                    <option value="negotiable">Negotiable</option>
                  </select>
                </div>

                {/* Message */}
                <div className="sm:col-span-2">
                  <label className="block text-xs font-600 text-foreground mb-1.5">Additional Details</label>
                  <p className="text-[11px] text-muted-foreground mb-1.5">Performance duration, special requirements, technical rider needs, etc.</p>
                  <textarea
                    {...register('message')}
                    rows={4}
                    placeholder="Tell us more about the event, your vision, and any specific requirements..."
                    className="w-full px-4 py-2.5 text-sm bg-input border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring focus:border-primary/40 transition-all resize-none"
                  />
                </div>
              </div>

              <div className="mt-6 flex items-center justify-between">
                <p className="text-xs text-muted-foreground">
                  <span className="text-negative">*</span> Required fields
                </p>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex items-center gap-2.5 px-6 py-3 rounded-xl bg-gradient-gold text-primary-foreground text-sm font-700 hover:opacity-90 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-150 min-w-[160px] justify-center"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send size={16} />
                      Submit Inquiry
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}